import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { faLock, faUser } from "@fortawesome/free-solid-svg-icons"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { PATH_MAP } from "../../configuration/PathMap"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { PostRequest } from "../../services/ApiClientService"
import type { SignInResponseDto } from "../../Objects/Generics/SignInResponseDto"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import logo from "../../assets/images/Logo2.png"
import InputField from "../../components/InputField/InputField"
import Navbar from "../../components/LandPage/Navbar"
import { useAdminEmployeeLogged } from "../../context/useAdminEmployeeContext"
import { useGetAdminEmployeeProfile } from "../../hooks/AdminEmployee/useGetAdminEmployeeProfile"

export const AdminLoginPage: React.FC = () => {
  const [username, setUsername] = useState("")

  const [password, setPassword] = useState("")

  const navigate = useNavigate()

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const { setAdminEmployeeProfile } = useAdminEmployeeLogged()

  const { handleGetAdminEmployeeProfile } = useGetAdminEmployeeProfile()

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    enableLoad()

    try {
      const response = await PostRequest<SignInResponseDto>(API_ROUTES.AUTH.ADMIN_LOGIN, {
        username,
        password,
      })

      if (!response.success || !response.data?.token) {
        throw new Error(response.message)
      }

      localStorage.setItem(API_ROUTES.TOKEN_ADMIN, JSON.stringify(response.data.token))

      await handleGetAdminEmployeeProfile(setAdminEmployeeProfile)

      navigate(PATH_MAP.ADMIN_DASHBOARD_PAGE, { replace: true })
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Erro ao autenticar administrador."
      setError("Acesso administrativo", message)
    } finally {
      disableLoad()
    }
  }

  return (
    <Animate type={AnimationTypes.slideDownIn} duration="0.5s">
      <div className="min-h-screen flex flex-col items-center justify-center relative bg-white overflow-hidden">
        <Navbar showFunctions={false} showWhatsAppFloat={false} />

        <div className="w-full max-w-md p-8 md:p-6 mt-10">
          <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
            <div className="mb-6 flex flex-col items-center text-center">
              <img src={logo} alt="Logo catalojei" className="mb-4 w-52 drop-shadow-xl md:w-64" />

              <h1 className="text-2xl font-bold text-gray-700 md:text-2xl">Painel Administrativo</h1>

              <p className="mt-2 max-w-sm text-sm leading-relaxed font-bold text-gray-500">
                Acesso exclusivo para administrador global da plataforma.
              </p>
            </div>
          </Animate>

          <Animate type={AnimationTypes.slideUpIn} duration="1.8s">
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <InputField
                  icon={faUser}
                  value={username}
                  type="text"
                  placeholder="E-mail ou usuário"
                  animationTypes={AnimationTypes.slideUpIn}
                  onChange={(e) => setUsername(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.admin.adminLoginPage.input.eMailOuUsuario"
                />

                <InputField
                  icon={faLock}
                  value={password}
                  type="password"
                  placeholder="Senha"
                  animationTypes={AnimationTypes.slideUpIn}
                  onChange={(e) => setPassword(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.admin.adminLoginPage.input.senha"
                />
              </div>

              <button
                type="submit"
                className="mt-6 w-full rounded-md bg-gradient-to-r from-orange-500 to-red-500 px-4 py-3 text-sm font-bold text-white shadow-lg transition-all duration-200 hover:scale-[1.01] hover:from-orange-400 hover:to-red-400"
                data-interaction-description-key="interactionDescriptions:pages.admin.adminLoginPage.button.entrarNoPainel"
              >
                Entrar no painel
              </button>
            </form>
          </Animate>
        </div>
      </div>
    </Animate>
  )
}
